import { useRef, useState } from 'react'
import type { VocabularyDialogState } from '../components/VocabularyDialog'
import {
  findResolvedVocabulary,
  recordVocabulary,
  setVocabularySaved,
} from '../data/db'
import {
  lookupVocabulary,
  type TextLookupSelection,
} from '../data/vocabularyLookup'
import type { IssueItem, VocabularyEntry, VocabularyOrigin, VocabularyState } from '../types/daily'

interface LookupRequest {
  issueId: string
  issueDate: string
  item: IssueItem
  selection: TextLookupSelection
}

const entryFromState = (state: VocabularyState, surfaceTerm: string): VocabularyEntry => ({
  term: surfaceTerm,
  lemma: state.lemma ?? state.normalizedTerm,
  aliases: state.aliases,
  phonetic: state.phonetic,
  partOfSpeech: state.partOfSpeech,
  difficulty: state.difficulty,
  meaningZh: state.meaningZh,
  meaningInContextZh: state.meaningInContextZh,
  collocation: state.collocation,
})

export function useVocabularyLookup() {
  const [lookup, setLookup] = useState<VocabularyDialogState>()
  const activeRequest = useRef<LookupRequest | undefined>(undefined)
  const requestSequence = useRef(0)

  const openCurated = async (issueId: string, issueDate: string, item: IssueItem, vocabulary: VocabularyEntry) => {
    activeRequest.current = undefined
    const next = await recordVocabulary(issueId, issueDate, item, vocabulary, {
      origin: 'curated',
      lookupStatus: 'resolved',
    })
    setLookup({ status: 'resolved', term: vocabulary.term, vocabulary: next })
    return next
  }

  const runLookup = async (request: LookupRequest) => {
    activeRequest.current = request
    const sequence = ++requestSequence.current
    setLookup({
      status: 'loading',
      term: request.selection.term,
      contextExcerpt: request.selection.contextExcerpt,
    })
    const result = await lookupVocabulary(request.issueDate, request.item, request.selection)
    if (sequence !== requestSequence.current) return undefined

    if (result.status === 'resolved') {
      const next = await recordVocabulary(
        request.issueId,
        request.issueDate,
        request.item,
        result.vocabulary,
        {
          normalizedTerm: result.normalizedTerm,
          surfaceNormalized: request.selection.normalized,
          origin: result.origin,
          lookupStatus: 'resolved',
          contextExcerpt: request.selection.contextExcerpt,
          dictionaryVersion: result.dictionaryVersion,
        },
      )
      if (sequence === requestSequence.current) {
        setLookup({
          status: 'resolved',
          term: request.selection.term,
          contextExcerpt: request.selection.contextExcerpt,
          vocabulary: next,
        })
      }
      return next
    }

    const local = await findResolvedVocabulary(request.selection.term)
    if (sequence !== requestSequence.current) return undefined
    if (local) {
      const origin = (local.origin === 'pending' ? 'dictionary' : local.origin ?? 'dictionary') as Exclude<VocabularyOrigin, 'pending'>
      const next = await recordVocabulary(
        request.issueId,
        request.issueDate,
        request.item,
        entryFromState(local, request.selection.term),
        {
          normalizedTerm: local.normalizedTerm,
          surfaceNormalized: request.selection.normalized,
          origin,
          lookupStatus: 'resolved',
          contextExcerpt: request.selection.contextExcerpt,
          dictionaryVersion: local.dictionaryVersion,
        },
      )
      if (sequence === requestSequence.current) {
        setLookup({
          status: 'resolved',
          term: request.selection.term,
          contextExcerpt: request.selection.contextExcerpt,
          vocabulary: next,
        })
      }
      return next
    }

    setLookup({
      status: result.status,
      term: request.selection.term,
      contextExcerpt: request.selection.contextExcerpt,
    })
    return undefined
  }

  const lookupSelection = (issueId: string, issueDate: string, item: IssueItem, selection: TextLookupSelection) => runLookup({
    issueId,
    issueDate,
    item,
    selection,
  })

  const retry = () => activeRequest.current ? runLookup(activeRequest.current) : Promise.resolve(undefined)

  const savePending = async () => {
    const request = activeRequest.current
    if (!request) return undefined
    const pending: VocabularyEntry = {
      term: request.selection.term,
      difficulty: 'unrated',
      meaningZh: '释义待补充',
      meaningInContextZh: '已保存选中语境；联网后可在词汇本中重新查词。',
      collocation: '',
    }
    const next = await recordVocabulary(request.issueId, request.issueDate, request.item, pending, {
      normalizedTerm: request.selection.normalized,
      surfaceNormalized: request.selection.normalized,
      origin: 'pending',
      lookupStatus: 'pending',
      contextExcerpt: request.selection.contextExcerpt,
      saved: true,
    })
    setLookup(undefined)
    activeRequest.current = undefined
    return next
  }

  const toggleSaved = async (saved: boolean) => {
    const vocabulary = lookup?.vocabulary
    if (!vocabulary) return undefined
    await setVocabularySaved(vocabulary.normalizedTerm, saved)
    setLookup((current) => current?.vocabulary
      ? { ...current, vocabulary: { ...current.vocabulary, saved } }
      : current)
    return saved
  }

  const close = () => {
    requestSequence.current += 1
    activeRequest.current = undefined
    setLookup(undefined)
  }

  return { lookup, openCurated, lookupSelection, retry, savePending, toggleSaved, close }
}
